import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import "./IDE.css";

const APP_NAV_HEIGHT = 5;
const IDE_NAV_HEIGHT = 48;
const DEFAULT_LOG_HEIGHT = 160;

export default function AdvancedIDE({ projectId, mode, project }) {
    const navigate = useNavigate();

    // =========================================================
    // VS Code
    // =========================================================

    const [vscodeUrl, setVscodeUrl] = useState("");
    const [launchError, setLaunchError] = useState("");

    // =========================================================
    // Fullscreen
    // =========================================================

    const [fullscreen, setFullscreen] = useState(false);

    // =========================================================
    // Build
    // =========================================================

    const [buildLogs, setBuildLogs] = useState("");
    const [simLogs, setSimLogs] = useState("");
    const [gdsLogs, setGdsLogs] = useState("");

    const [buildDone, setBuildDone] = useState(false);
    const [isBuilding, setIsBuilding] = useState(false);

    // =========================================================
    // Log panel
    // =========================================================

    const [logHeight, setLogHeight] =
        useState(DEFAULT_LOG_HEIGHT);

    const [dragging, setDragging] = useState(false);
    const [logTab, setLogTab] = useState("build");

    const dragStartY = useRef(0);
    const startHeight = useRef(logHeight);
    const logEndRef = useRef(null);

    // =========================================================
    // Launch Advanced IDE
    // =========================================================

    useEffect(() => {
        let cancelled = false;

        async function launchIDE() {
            try {
                setLaunchError("");

                const res = await fetch("/ide/launch", {
                    credentials: "include",
                });

                if (!res.ok) {
                    throw new Error(
                        `IDE launch failed: HTTP ${res.status}`
                    );
                }

                const data = await res.json();

                if (!data.success) {
                    throw new Error(
                        data.message || "Failed to launch IDE"
                    );
                }

                if (!cancelled) {
                    setVscodeUrl(data.ideurl);
                }
            } catch (error) {
                console.error("Advanced IDE launch failed:", error);

                if (!cancelled) {
                    setLaunchError(
                        error.message || "Failed to launch IDE"
                    );
                }
            }
        }

        launchIDE();

        return () => {
            cancelled = true;
        };
    }, []);

    // =========================================================
    // Build log polling
    // =========================================================

    useEffect(() => {
        if (!isBuilding) {
            return;
        }

        const interval = setInterval(async () => {
            try {
                const res = await fetch("/api/build/logs", {
                    credentials: "include",
                });

                if (!res.ok) {
                    throw new Error(`HTTP ${res.status}`);
                }

                const data = await res.json();

                setBuildLogs(data.logs || "");

                if (data.status === "done") {
                    setIsBuilding(false);
                    setBuildDone(true);
                }
            } catch (error) {
                console.error(
                    "Build polling failed:",
                    error
                );
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [isBuilding]);

    // =========================================================
    // Auto-scroll logs
    // =========================================================

    useEffect(() => {
        logEndRef.current?.scrollIntoView({
            behavior: "smooth",
        });
    }, [
        buildLogs,
        simLogs,
        gdsLogs,
        logTab,
    ]);

    // =========================================================
    // Log dragging
    // =========================================================

    const handleMouseDown = (event) => {
        setDragging(true);

        dragStartY.current = event.clientY;
        startHeight.current = logHeight;

        document.body.style.cursor = "ns-resize";
    };

    useEffect(() => {
        if (!dragging) {
            return;
        }

        const handleMouseMove = (event) => {
            const delta =
                dragStartY.current - event.clientY;

            const newHeight = Math.min(
                Math.max(
                    80,
                    startHeight.current + delta
                ),
                window.innerHeight - 120
            );

            setLogHeight(newHeight);
        };

        const handleMouseUp = () => {
            setDragging(false);
            document.body.style.cursor = "default";
        };

        window.addEventListener(
            "mousemove",
            handleMouseMove
        );

        window.addEventListener(
            "mouseup",
            handleMouseUp
        );

        return () => {
            window.removeEventListener(
                "mousemove",
                handleMouseMove
            );

            window.removeEventListener(
                "mouseup",
                handleMouseUp
            );

            document.body.style.cursor = "default";
        };
    }, [dragging]);

    // =========================================================
    // BUILD
    // =========================================================

    const buildJar = async () => {
        try {
            setBuildDone(false);

            setBuildLogs("");
            setSimLogs("");
            setGdsLogs("");

            setLogTab("build");
            setIsBuilding(true);

            const res = await fetch("/api/build", {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    lab_id: project.lab_id,
                }),
            });

            const data = await res.json();

            if (!res.ok) {
                setIsBuilding(false);
                setBuildDone(false);
                setBuildLogs(
                    data.message || "Build failed."
                );
            }
        } catch (error) {
            console.error(
                "Build request failed:",
                error
            );

            setIsBuilding(false);
            setBuildDone(false);

            setBuildLogs(
                "Failed to start build: " +
                error.message
            );
        }
    };

    // =========================================================
    // STOP BUILD
    // =========================================================

    const stopBuild = async () => {
        try {
            const res = await fetch(
                "/api/build/stop",
                {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        lab_id: project.lab_id,
                    }),
                }
            );

            if (!res.ok) {
                throw new Error(
                    `HTTP ${res.status}`
                );
            }
        } catch (error) {
            console.error(
                "Failed to stop build:",
                error
            );
        }

        setIsBuilding(false);
        setBuildDone(false);
    };

    // =========================================================
    // SIMULATION
    // =========================================================

    const simulate = async () => {
        if (!buildDone) {
            setSimLogs(
                "Please build successfully before starting the simulation."
            );

            setLogTab("simulation");
            return;
        }

        try {
            setSimLogs("Starting simulation...");
            setLogTab("simulation");

            const res = await fetch(
                "/api/simulate",
                {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        "Content-Type":
                            "application/json",
                    },
                    body: JSON.stringify({
                        lab_id: project.lab_id,
                    }),
                }
            );

            const data = await res.json();

            if (!res.ok) {
                setSimLogs(
                    data.message ||
                    "Simulation failed."
                );
                return;
            }

            if (!data.vncUrl) {
                setSimLogs(
                    "Simulation started but no VNC URL was returned."
                );
                return;
            }

            navigate(
                `/rect/simulation/${project.lab_id}/${project.id}`
            );
        } catch (error) {
            console.error(
                "Simulation request failed:",
                error
            );

            setSimLogs(
                "Failed to start simulation: " +
                error.message
            );
        }
    };

    // =========================================================
    // RUN GDS
    // =========================================================

    const runRobot = async () => {
        try {
            const res = await fetch(
                "/api/run_gds",
                {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        "Content-Type":
                            "application/json",
                    },
                    body: JSON.stringify({
                        lab_id: project.lab_id,
                    }),
                }
            );

            if (!res.ok) {
                throw new Error(
                    `HTTP ${res.status}`
                );
            }
        } catch (error) {
            console.error(
                "Failed to run GDS:",
                error
            );
        }
    };

    // =========================================================
    // SAVE PROJECT
    // =========================================================

    const gitPushProject = async () => {
        try {
            const response = await fetch(
                "/api/project/git/push",
                {
                    method: "POST",
                    headers: {
                        "Content-Type":
                            "application/json",
                    },
                    credentials: "include",
                    body: JSON.stringify({
                        // Add projectId here when available
                    }),
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(
                    data.error ||
                    "Failed to save code"
                );
            }

            console.log(
                "Code saved:",
                data
            );

            alert(
                "Code saved successfully."
            );
        } catch (error) {
            console.error(
                "Save code error:",
                error
            );

            alert(
                error.message ||
                "Failed to save code."
            );
        }
    };

    // =========================================================
    // PROJECTS
    // =========================================================

    const goProject = () => {
        window.location.href =
            `/rect/app/projects/${project.lab_id}`;
    };

    // =========================================================
    // IDE launch error
    // =========================================================

    if (launchError) {
        return (
            <div className="container mt-5">
                <h4>Advanced IDE Error</h4>
                <p>{launchError}</p>
            </div>
        );
    }

    // =========================================================
    // Layout
    // =========================================================

    const appNavHeight =
        fullscreen ? 0 : APP_NAV_HEIGHT;

    const ideNavHeight =
        fullscreen ? 0 : IDE_NAV_HEIGHT;

    const bottomOffset =
        fullscreen ? 0 : logHeight;

    const topOffset =
        appNavHeight + ideNavHeight;

    // =========================================================
    // RENDER
    // =========================================================

    return (
        <>
            {/* =================================================
                Toolbar
            ================================================= */}

            {!fullscreen && (
                <div
                    className="ide-toolbar"
                    style={{
                        top: appNavHeight,
                    }}
                >
                    <div className="ide-title">
                        IDE (advanced)
                    </div>

                    <button
                        className="ide-button ide-button-build"
                        disabled={isBuilding}
                        onClick={buildJar}
                    >
                        {isBuilding
                            ? "Building..."
                            : "Build"}
                    </button>

                    <button
                        className="ide-button ide-button-simulate"
                        disabled={
                            isBuilding ||
                            !buildDone
                        }
                        onClick={simulate}
                        title={
                            buildDone
                                ? "Start ROS simulation"
                                : "Build successfully before starting simulation"
                        }
                    >
                        Simulate ROS
                    </button>

                    {isBuilding && (
                        <button
                            className="ide-button ide-button-danger"
                            onClick={stopBuild}
                        >
                            Stop Build
                        </button>
                    )}

                    <button
                        className="ide-button ide-button-secondary ide-fullscreen-button"
                        onClick={gitPushProject}
                    >
                        Save
                    </button>

                    <button
                        className="ide-button ide-button-secondary ide-fullscreen-button"
                        onClick={goProject}
                    >
                        Projects
                    </button>
                </div>
            )}

            {/* =================================================
                VS Code
            ================================================= */}

            <div
                className="ide-content"
                style={{
                    top:
                        topOffset +
                        (!fullscreen ? 36 : 0),
                    bottom: bottomOffset,
                }}
            >
                {vscodeUrl ? (
                    <iframe
                        src={vscodeUrl}
                        title="VS Code"
                        className="ide-frame"
                    />
                ) : (
                    <div className="mt-5 text-center">
                        Starting Advanced IDE…
                    </div>
                )}
            </div>

            {/* =================================================
                Logs
            ================================================= */}

            {!fullscreen && (
                <div className="ide-log-panel">

                    <div
                        className="ide-log-drag-handle"
                        onMouseDown={
                            handleMouseDown
                        }
                    />

                    <div className="ide-log-tabs">
                        {[
                            "BUILD",
                            "SIMULATION",
                            "GDS",
                        ].map((tab) => {
                            const key =
                                tab.toLowerCase();

                            return (
                                <button
                                    key={tab}
                                    className={
                                        logTab === key
                                            ? "ide-log-tab active"
                                            : "ide-log-tab"
                                    }
                                    onClick={() =>
                                        setLogTab(key)
                                    }
                                >
                                    {tab}
                                </button>
                            );
                        })}
                    </div>

                    <pre
                        className="ide-log-output"
                        style={{
                            height:
                                logHeight - 40,
                        }}
                    >
                        {logTab === "build" &&
                            buildLogs}

                        {logTab === "simulation" &&
                            simLogs}

                        {logTab === "gds" &&
                            gdsLogs}

                        <div ref={logEndRef} />
                    </pre>
                </div>
            )}
        </>
    );
}